import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, ShieldCheck, Send, Sparkles } from "lucide-react";
import Logo from "@/components/Logo";
import { API_BASE } from "@/lib/site";
import { initTracking, trackConversion, pushLeadDataLayer } from "@/lib/tracking";
import { getTrackingPayload } from "@/lib/utm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/* Slugify funnel option labels into stable, clean test-ids
 * (lowercase, alphanumerics + single hyphens, trimmed). */
const slugifyOption = (s) =>
  String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

/* Reusable single-question-at-a-time funnel for ad landing pages. */
export default function Funnel({ slug, hero, subtitle, accentBadge, questions, finalTitle, eventName }) {
  const totalSteps = questions.length + 1; // questions + final form
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({
    full_name: "",
    business_name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  /* Load tracking pixels once when funnel mounts; conversion fires only on success. */
  useEffect(() => {
    initTracking();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- initTracking is module-level, intentionally fires once on mount

  const onPick = (qLabel, value) => {
    setAnswers((a) => ({ ...a, [qLabel]: value }));
    // smooth-advance after a brief delay so users see selection
    setTimeout(() => setStep((s) => Math.min(s + 1, totalSteps - 1)), 220);
  };

  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const submit = async (e) => {
    e.preventDefault();
    if (!contact.full_name || !contact.phone || !contact.email) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_BASE}/funnel`, {
        funnel: slug,
        ...contact,
        answers,
        tracking: getTrackingPayload(),
      });
      const leadId = r?.data?.id;
      // Fire the ad conversion ONLY after a successful backend submit.
      if (eventName) trackConversion(eventName);
      // Fire GTM dataLayer event only on successful submit. The event name
      // maps 1:1 to the funnel slug so Marketing can gate GTM tags on it.
      if (slug === "free-pos-offer") {
        pushLeadDataLayer("lead_free_pos_submit", "free_pos", leadId);
      } else if (slug === "cash-discount-savings") {
        pushLeadDataLayer("lead_cash_discount_submit", "cash_discount", leadId);
      }
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const isLastStep = step === questions.length;
  const progressPct = Math.round(((step + 1) / totalSteps) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[var(--swipe-accent-soft)] via-white to-white" data-testid={`funnel-${slug}`}>
      {/* Minimal funnel header */}
      <header className="bg-white border-b border-line">
        <div className="container-swipe flex items-center justify-between py-5">
          <Logo variant="dark" className="w-[180px] sm:w-[220px] h-auto" testId="funnel-logo" />
          <div className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--swipe-accent-soft)] border border-[var(--swipe-accent)]/20 text-[10px] uppercase tracking-[0.22em] font-extrabold text-[var(--swipe-accent)]">
            <ShieldCheck className="w-3 h-3" strokeWidth={3} />
            Secure Submission
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="container-swipe pt-12 pb-6 text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--swipe-accent-soft)] border border-[var(--swipe-accent)]/25 text-[10.5px] uppercase tracking-[0.22em] font-extrabold text-[var(--swipe-accent)] mb-5">
          <Sparkles className="w-3 h-3" strokeWidth={3} />
          {accentBadge}
        </div>
        <h1 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold leading-[1.1] text-[var(--swipe-navy)] tracking-[-0.02em]">
          {hero}
        </h1>
        <p className="mt-5 text-[16px] sm:text-[17px] text-slate-600 leading-[1.7]">
          {subtitle}
        </p>
      </section>

      {/* Card */}
      <section className="container-swipe pb-20">
        <div className="max-w-2xl mx-auto bg-white border border-line rounded-3xl shadow-[0_30px_70px_-32px_rgba(6,26,58,0.30)] overflow-hidden">
          {/* Progress */}
          <div className="px-7 sm:px-9 pt-7">
            {!done && (
              <div className="flex items-center justify-between text-[11.5px] font-extrabold uppercase tracking-[0.18em] text-[var(--swipe-accent)]">
                <span>Step {Math.min(step + 1, totalSteps)} of {totalSteps}</span>
                <span className="text-slate-400">{progressPct}%</span>
              </div>
            )}
            {!done && (
              <div className="mt-3 h-1.5 rounded-full bg-[var(--swipe-accent-soft)] overflow-hidden">
                <div
                  className="h-full bg-[var(--swipe-accent)] rounded-full transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            )}
          </div>

          <div className="px-6 sm:px-9 py-8">
            {done ? (
              <div className="text-center py-8" data-testid={`${slug}-success`}>
                <div className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-[var(--swipe-accent)] text-white shadow-[0_18px_36px_-10px_rgba(0,114,255,0.55)]">
                  <Check className="w-8 h-8" strokeWidth={3} />
                </div>
                <h2 className="mt-6 font-display-tight text-2xl sm:text-3xl font-extrabold text-[var(--swipe-navy)]">
                  Thank you for contacting Swipetronics.
                </h2>
                <p className="mt-3 text-slate-600 text-[15.5px] leading-[1.7] max-w-md mx-auto">
                  We received your information and will review it shortly. A specialist will contact you to discuss your processing options, POS needs, and possible savings.
                </p>
              </div>
            ) : !isLastStep ? (
              <Question
                slug={slug}
                index={step}
                q={questions[step]}
                selected={answers[questions[step].label]}
                onPick={onPick}
              />
            ) : (
              <FinalForm
                slug={slug}
                contact={contact}
                setContact={setContact}
                submit={submit}
                submitting={submitting}
                finalTitle={finalTitle}
              />
            )}
          </div>

          {/* Footer nav */}
          {!done && (
            <div className="px-6 sm:px-9 pb-7 flex items-center justify-between border-t border-line pt-5">
              <button
                onClick={goBack}
                disabled={step === 0}
                className="inline-flex items-center gap-2 text-[13px] font-bold text-slate-500 hover:text-[var(--swipe-navy)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid={`${slug}-back`}
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <span className="text-[11.5px] text-slate-400 font-semibold">
                {isLastStep ? "Last step" : "Pick an answer to continue"}
              </span>
            </div>
          )}
        </div>

        <p className="max-w-2xl mx-auto mt-5 text-center text-[12px] text-slate-500 leading-relaxed">
          By submitting this form, you agree to be contacted by Swipetronics regarding your request. Message and data rates may apply. Reply STOP to opt out of text messages.
          <br />
          See our{" "}
          <a href="/privacy" className="text-[var(--swipe-accent)] underline hover:opacity-80">Privacy Policy</a>
          {" "}and{" "}
          <a href="/terms" className="text-[var(--swipe-accent)] underline hover:opacity-80">Terms of Service</a>.
        </p>
      </section>

      {/* Minimal funnel footer — single brand line + legal links. Intentionally
       * NOT the full SiteLayout footer so this conversion-focused page stays
       * lean. Same on /free-pos-offer + /cash-discount-savings via Funnel.jsx. */}
      <footer
        data-testid="funnel-footer"
        className="border-t border-slate-200 bg-white py-5"
      >
        <div className="container-swipe">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-3 text-[12px] text-slate-500">
            <span className="font-semibold text-[var(--swipe-navy)]">
              Powered by Swipetronics &mdash; 2026
            </span>
            <span className="hidden sm:inline text-slate-300">|</span>
            <a
              href="/privacy"
              data-testid="funnel-footer-privacy"
              className="hover:text-[var(--swipe-accent)] hover:underline transition-colors"
            >
              Privacy Policy
            </a>
            <span className="hidden sm:inline text-slate-300">|</span>
            <a
              href="/terms"
              data-testid="funnel-footer-terms"
              className="hover:text-[var(--swipe-accent)] hover:underline transition-colors"
            >
              Terms of Service
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Question({ slug, index, q, selected, onPick }) {
  return (
    <div data-testid={`${slug}-q-${index + 1}`}>
      <h2 className="font-display-tight text-[1.4rem] sm:text-[1.7rem] font-extrabold text-[var(--swipe-navy)] leading-[1.2] tracking-[-0.015em]">
        {q.label}
      </h2>
      <div className="mt-7 grid gap-3">
        {q.options.map((opt) => {
          const active = selected === opt;
          return (
            <button
              key={opt}
              onClick={() => onPick(q.label, opt)}
              data-testid={`${slug}-q${index + 1}-${slugifyOption(opt)}`}
              className={`group relative flex items-center justify-between gap-4 w-full px-5 py-4 sm:px-6 sm:py-5 rounded-2xl border-2 text-left transition-all duration-200 ${
                active
                  ? "border-[var(--swipe-accent)] bg-[var(--swipe-accent-soft)] shadow-[0_14px_30px_-14px_rgba(0,114,255,0.45)]"
                  : "border-line bg-white hover:border-[var(--swipe-accent)]/55 hover:bg-[var(--swipe-accent-soft)]/45 hover:-translate-y-0.5 hover:shadow-[0_12px_26px_-14px_rgba(0,114,255,0.35)]"
              }`}
            >
              <span className="font-bold text-[15px] sm:text-[16px] text-[var(--swipe-navy)] leading-tight">
                {opt}
              </span>
              <span
                className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 ${
                  active
                    ? "bg-[var(--swipe-accent)] text-white"
                    : "bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] group-hover:bg-[var(--swipe-accent)] group-hover:text-white"
                }`}
              >
                {active ? <Check className="w-4 h-4" strokeWidth={3} /> : <ArrowRight className="w-4 h-4" strokeWidth={2.6} />}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FinalForm({ slug, contact, setContact, submit, submitting, finalTitle }) {
  const set = (k) => (e) => setContact({ ...contact, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="grid gap-5" data-testid={`${slug}-final-form`}>
      <h2 className="font-display-tight text-[1.45rem] sm:text-[1.7rem] font-extrabold text-[var(--swipe-navy)] leading-[1.2] tracking-[-0.015em]">
        {finalTitle || "Almost done — where should we send your review?"}
      </h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="fn">Full Name *</Label>
          <Input id="fn" required value={contact.full_name} onChange={set("full_name")} className="mt-2" data-testid={`${slug}-full-name`} />
        </div>
        <div>
          <Label htmlFor="bn">Business Name</Label>
          <Input id="bn" value={contact.business_name} onChange={set("business_name")} className="mt-2" data-testid={`${slug}-business-name`} />
        </div>
        <div>
          <Label htmlFor="ph">Phone Number *</Label>
          <Input id="ph" required value={contact.phone} onChange={set("phone")} className="mt-2" data-testid={`${slug}-phone`} />
        </div>
        <div>
          <Label htmlFor="em">Email Address *</Label>
          <Input id="em" type="email" required value={contact.email} onChange={set("email")} className="mt-2" data-testid={`${slug}-email`} />
        </div>
      </div>
      <div>
        <Label htmlFor="msg">Optional message</Label>
        <Textarea id="msg" rows={3} value={contact.message} onChange={set("message")} className="mt-2" data-testid={`${slug}-message`} />
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="btn-primary-swipe w-full justify-center text-[15px] py-4 disabled:opacity-60"
        data-testid={`${slug}-submit`}
      >
        {submitting ? "Submitting…" : <>Submit My Request <Send className="w-4 h-4" /></>}
      </button>
    </form>
  );
}
